import Link from "next/link";

import { Button, css, styled, TextField } from "@mui/material";

import { getColor } from "@hooks";
import { cssForHeaderWithLinearGradient, cssPropertiesToString } from "@utils";

export const ContactFormCard = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 24px;
    justify-content: center;
    flex-grow: 1;

    padding: 32px;
    max-width: 616px;
    width: 100%;

    ${theme.palette.mode === "dark"
      ? css`
          background: ${getColor(c => c.tokens.bg["weak-50"], theme)};
        `
      : css`
          background: ${getColor(c => c.tokens.bg["white-0"], theme)};
        `}
    border: 1px solid ${getColor(c => c.tokens.stroke["soft-200"], theme)};
    border-radius: 16px;
  `,
);

export const SendMessageContainer = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;

    ${theme.breakpoints.up("tablet")} {
      flex-direction: row;
      justify-content: space-between;
    }
  `,
);

export const AlternativeMessageContainer = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 4px;

    ${theme.breakpoints.between("mobile", "tablet")} {
      width: 100%;
    }

    ${theme.breakpoints.up("tablet")} {
      align-items: flex-end;
    }
  `,
);

export const ContactFormTitle = styled("span")(
  ({ theme }) => css`
    width: fit-content;

    ${cssForHeaderWithLinearGradient(
      theme,
      "linear-gradient(82.22deg, #05DF54 0%, #96C5F0 76%)",
    )}

    ${theme.palette.mode === "dark" &&
    css`
      text-shadow: 0px 0px 20px rgba(51, 235, 131, 0.25);
    `}

    ${theme.breakpoints.up("mobile")} {
      ${cssPropertiesToString(theme.typography.heading_h1_bold_mobile)}
    }

    ${theme.breakpoints.up("tablet")} {
      ${cssPropertiesToString(theme.typography.display_semibold_small)}
    }
  `,
);

export const ContactFormText = styled("span")(
  ({ theme }) => css`
    color: ${getColor(c => c.tokens.text["sub-600"], theme)};
    ${cssPropertiesToString(theme.typography.paragraph_small_regular)}
  `,
);

export const ContactFormMail = styled("span")(
  ({ theme }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_medium_semibold)}
  `,
);

export const ContactFormPolicyLink = styled(Link)(
  ({ theme }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_small_special_underline)}
  `,
);

export const TextFieldStyled = styled(TextField)(
  () => css`
    & .MuiInputBase-root {
      padding-bottom: 32px !important;
    }
    & .MuiInputBase-input {
      padding-bottom: 0px !important;
    }
  `,
);

interface BottomLineStyledProps {
  error: boolean;
}

export const BottomLineStyled = styled("div")<BottomLineStyledProps>(
  ({ theme, error }) => css`
    position: absolute;
    bottom: ${error ? "23px" : "0"};
    right: 0;
    padding-right: 10px;
    padding-bottom: 10px;
    font-family: "Work Sans", sans-serif;
    font-weight: 500;
    font-size: 11px;
    line-height: 11px;
    color: ${getColor(c => c.tokens.text["soft-400"], theme)};
    display: flex;
    gap: 4px;
  `,
);

export const ContactFormButton = styled(Button)(
  ({ theme }) => css`
    color: ${getColor(c => c.tokens.text["white-0"], theme)};
    background: ${getColor(c => c.tokens.bg["strong-950"], theme)};
    &:hover {
      background-color: ${getColor(c => c.tokens.bg["surface-800"], theme)};
    }
    &.Mui-disabled {
      color: ${getColor(c => c.tokens.text["sub-600"], theme)};
    }
  `,
);
